import React, { useEffect } from 'react';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../../../components/Common/Notification';
import { useBulkLocationPushdownSearch, useBulkLocationPushdownSearchNoProject } from '../../../apis/bulkPushdown';
import { IdName } from '../../../apis/bulkPushdown/bulkPushdown.types';

interface AllLocationsFormProps {
	setSelectedLocations: (locations: IdName[]) => void;
	baselineId: string;
	baselineTaskIds: number[];
	projectId?: string;
	isProjectsActive: boolean;
	isProjectIdRequiredForLocationTask: boolean;
}

const AllLocationsForm: React.FC<AllLocationsFormProps> = ({
	setSelectedLocations,
	baselineId,
	baselineTaskIds,
	projectId,
	isProjectsActive,
	isProjectIdRequiredForLocationTask,
}) => {
	const shouldUseNoProjectSearch = isProjectsActive === true && isProjectIdRequiredForLocationTask === false && !projectId;

	const byProjectQuery = useBulkLocationPushdownSearch(
		{ baselineId, projectId },
		{ enabled: !shouldUseNoProjectSearch }
	);
	const noProjectQuery = useBulkLocationPushdownSearchNoProject(
		{ baselineId, baselineTaskIdList: baselineTaskIds },
		{ enabled: shouldUseNoProjectSearch }
	);

	const activeQuery = shouldUseNoProjectSearch ? noProjectQuery : byProjectQuery;
	const searchResults = activeQuery.data?.locations ?? [];

	useEffect(() => {
		if (!activeQuery.error) return;
		Notification('error', 'An Error Occurred', activeQuery.error.message);
	}, [activeQuery.error]);

	useEffect(() => {
		if (!activeQuery.data) return;
		if (activeQuery.data.isError) {
			Notification('error', 'An Error Occurred', activeQuery.data.errors?.[0]?.message ?? 'Failed to search locations');
			return;
		}
		setSelectedLocations(activeQuery.data.locations.slice());
		// setSelectedLocations identity is not stable across renders in the parent; only react to fresh data
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeQuery.data]);

	return (
		<div className="all-locations-form">
			<LoadingIndicator isLoading={activeQuery.isFetching}>
				<div className="row">
					<div className="col">
						<span className="has-float-label">
							<select id="locationsList" className="form-control" multiple size={25} disabled>
								{searchResults.map((loc) => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
							</select>
							<label htmlFor="locationsList">Locations to receive tasks</label>
						</span>
					</div>
				</div>
			</LoadingIndicator>
		</div>
	);
};

export default AllLocationsForm;
